// Offscreen map rendering for the PNG export. The view is fitted to the data
// (not to the on-screen viewport), the base layer's tiles are fetched with
// CORS and stitched into a canvas at their native resolution, then the overlay
// layers are painted with the same aggregation the live markers use.
import { mapPoints, pointsBounds } from './mapPoints';
import { tileJobs, viewFor, projectPoint, loadTileImage, TILE_SIZE } from './mapTiles';
import { drawMapLayers } from './mapPaint';
import { drawAttribution } from './mapShapes';
import { TILE_LAYERS } from './mapIcons';

// Surfaced to the user when no tile could be loaded (usually a tile server
// that does not send CORS headers, which also taints the canvas).
export const MAP_TILES_ERROR =
  'Could not load map tiles for export. The base layer may not allow cross-origin images — try another base layer.';

export async function renderMapCanvas({
  rows,
  fields,
  config = {},
  background = '#ffffff',
  labelColor = '#1c1b1f',
  width = 1600,
  height = 900,
  loadTile = loadTileImage
}) {
  const points = mapPoints(rows, fields, config);
  if (!points || points.length === 0) return null;

  const view = viewFor(pointsBounds(points), width, height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (background !== 'transparent') {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
  }

  const source = TILE_LAYERS[config.baseLayer] || TILE_LAYERS.osm;
  await paintTiles(ctx, { source, view, width, height, loadTile });
  drawMapLayers(ctx, {
    layers: config.layers,
    points,
    project: (lat, lon) => projectPoint(view, lat, lon),
    labelColor
  });
  drawAttribution(ctx, source.attribution, width, height);
  return canvas;
}

// Fetches every tile of the view and draws it at its computed offset.
async function paintTiles(ctx, { source, view, width, height, loadTile }) {
  const jobs = tileJobs(source, view, width, height);
  const images = await Promise.all(jobs.map((job) => loadTile(job.url)));
  if (jobs.length > 0 && images.every((image) => !image)) throw new Error(MAP_TILES_ERROR);
  images.forEach((image, index) => {
    if (!image) return;
    const job = jobs[index];
    ctx.drawImage(image, job.left, job.top, TILE_SIZE, TILE_SIZE);
  });
}
